import { overrideWSSendMethod } from "@amplio-common/funcs/websocketUtils";
import { WS_API_SERVER_URL } from "@app/api";
import { WS_ERROR_CODES, ROLES } from "@app/constants";
import { wsReconnect, wsConnected, setWSError, setLastPong, selectLastPong, wsClose } from "./websocketSlice";
import { currentUser } from "@app/routes/auth/selectors";
import { WS_TREATMENT_PING_INTERVAL, WS_STUDENT_LIVE_CHECK_INTERVAL, WRONG_TREATMENT_ERR_MSG, DUPLICATE_SIGN_IN_ERR_MSG, WS_ERROR_THRESHOLD, UNKNOWN_WS_ERROR, WS_TEACHER_LIVE_CHECK_INTERVAL } from "../constants";
import { handleCheckStudentConnectedStatus, handleTherapistWSMessage, handleTherapsitTreatmentActions } from "../websocket/websocketTherapist";
import { handleStudentWSMessage, handleStudentTreatmentActions, handleCheckTeacherConnectedStatus } from "../websocket/websocketStudent";
import { WS_COMMANDS } from "../websocket/websocketCommands";
import { selectFacilitatorStudent, selectTreatment, setUserStatuses } from "@app/routes/treatmentV3/store/treatmentServerDataSlice";
import { hiFromServerTime } from "./techStatusSlice";
import { setBoardMode } from "@app/routes/treatmentV3/store/contentThunks";
import { endVideoSession, setIsOpentokError, setIsVideoAllowed, setOpentokReconnect } from "./videoSlice";
import { toast } from "react-toastify";
import { Trans } from "@lingui/macro";

let pingInterval;
let reconnectTimeout;
let studentStatusCheckInterval;
let errorCounter = 0;
let teacherStatusCheckInterval;
let serverHiTime = null;
let pongInterval;
let showingBadNetwork = false;
const uniqId = Date.now().toString(36) + Math.random().toString(36).substring(2); // tricasdk to get 99.9999..% uniq id

const handlePing = (websocket) => {
  pingInterval && clearInterval(pingInterval);

  websocket.reduxStore.dispatch(setLastPong(Date.now()));

  pingInterval = setInterval(() => {
    if (websocket.readyState !== WebSocket.OPEN) { return; }
    websocket.send(JSON.stringify({ command: WS_COMMANDS.PING }));
  }, WS_TREATMENT_PING_INTERVAL);
};

const onOpenTherapistRole = (websocket) => {
  studentStatusCheckInterval = setInterval(() => handleCheckStudentConnectedStatus(websocket), WS_STUDENT_LIVE_CHECK_INTERVAL);
};

const onOpenStudentRole = (websocket) => {
  websocket.send(JSON.stringify({ command: WS_COMMANDS.REQUEST_CURRENT_CONTENT }));
  teacherStatusCheckInterval = setInterval(() => handleCheckTeacherConnectedStatus(websocket), WS_TEACHER_LIVE_CHECK_INTERVAL);
};

const onOpen = (event) => {
  const self = event.srcElement;
  self.reduxStore.dispatch(setOpentokReconnect(true));
  serverHiTime = Date.now();
  self.uniqId = uniqId;

  let credType = "JWT";
  if (localStorage.getItem("code_login_token")) { credType = "CODE"; }
  if (localStorage.getItem("class_login_token")) { credType = "CLASS"; }
  //const credType = localStorage.getItem("totp_token") || localStorage.getItem("access_token") ?  : "CODE";


  const credToken = localStorage.getItem("access_token") || localStorage.getItem("totp_token") || localStorage.getItem("code_login_token") || localStorage.getItem("class_login_token");
  self.originalSend(`${credType} ${credToken}`);

  self.reduxStore.dispatch(wsConnected({ websocket: self }));
  // trackEvent("session_ws_open", { treatment_id }); // TODO
  handlePing(self);
  const currUser = currentUser(self.reduxStore.getState());

  self.reduxStore.dispatch(setIsVideoAllowed(true));
  self.originalSend(JSON.stringify({ command: WS_COMMANDS.HI, uniqId: self.uniqId }));
  currUser.role === ROLES.THERAPIST.id && onOpenTherapistRole(self);
  [ROLES.STUDENT.id, ROLES.CLINIC_ADMINISTRATOR.id].includes(currUser.role) && onOpenStudentRole(self);
};

const onClose = (event) => {
  const self = event.srcElement;
  
  clearTimeout(reconnectTimeout);
  clearTimeout(studentStatusCheckInterval);
  clearTimeout(teacherStatusCheckInterval);

  if (event.code === WS_ERROR_CODES.WS_PERMISSION_DENIED) {
    self.reduxStore.dispatch(setWSError(WRONG_TREATMENT_ERR_MSG));
    return;
  }

  if ([WS_ERROR_CODES.WS_UNKNOWN_EXCEPTION_CODE, WS_ERROR_CODES.WS_GAIERR_CODE].includes(event.code)) {
    console.error(event.code);
    errorCounter += 1;
    if (errorCounter === WS_ERROR_THRESHOLD) {
      errorCounter = 0;
      self.reduxStore.dispatch(setWSError(UNKNOWN_WS_ERROR));
    }
  }

  self.close(1000);
};

const onError = (event) => {
  console.error("ws error", event);
  if (event.srcElement.readyState === WebSocket.OPEN) return;
  clearInterval(pingInterval);
  reconnectTimeout = setTimeout(() => { event.srcElement.reduxStore.dispatch(wsReconnect()); }, 1000);
};

const onMessage = (event) => {
  const self = event.srcElement;
  const wsPayload = JSON.parse(event.data);

  const currUser = currentUser(self.reduxStore.getState());
  const facilitatorStudent = selectFacilitatorStudent(self.reduxStore.getState());

  if (wsPayload?.content?.command === WS_COMMANDS.HI && wsPayload?.content?.user_id === currUser.id && wsPayload?.content?.uniqId !== self.uniqId) {
    self.reduxStore.dispatch(setWSError(DUPLICATE_SIGN_IN_ERR_MSG));
    return;
  }
  
  // ignore messages from self
  const myId = currUser.role === ROLES.CLINIC_ADMINISTRATOR.id ? facilitatorStudent?.student_id : currUser.id;

  if (wsPayload?.command === WS_COMMANDS.PONG) {
    showingBadNetwork = false;
    self.reduxStore.dispatch(setLastPong(Date.now()));
  }

  if (wsPayload?.content?.command === WS_COMMANDS.HI && wsPayload?.content?.user_id === myId) {
    if (serverHiTime) {
      serverHiTime = Date.now() - serverHiTime;
      self.reduxStore.dispatch(hiFromServerTime(serverHiTime));
    }
    return;
  }

  if (wsPayload?.content?.user_id === myId) { return; }

  currUser.role === ROLES.THERAPIST.id && handleTherapistWSMessage(self, wsPayload);
  [ROLES.STUDENT.id, ROLES.CLINIC_ADMINISTRATOR.id].includes(currUser.role) && handleStudentWSMessage(self, wsPayload);
};

const initWS = (url, store) => {
  const websocket = new WebSocket(url);
  overrideWSSendMethod(websocket);

  websocket.reduxStore = store;
  websocket.onopen = onOpen;
  websocket.onclose = onClose;
  websocket.onerror = onError;
  websocket.onmessage = onMessage;

  return websocket;
};

const createWebsocket = (store, action) => {
  const { treatmentId, studentId } = action.payload;

  const urlSuffix = studentId ? `${studentId}/` : "";
  const url = `${WS_API_SERVER_URL}/ws/treatments/${treatmentId}/${urlSuffix}`;
  return initWS(url, store);
};

const wsMiddleware = () => {
  let socket = null;

  return store => next => action => {
    if (
      !action.type.startsWith("websocketSlice")
      && !(action.type.startsWith("treatmentServerDataSlice"))
      && !(action.type.startsWith("administrationSlice"))
      && !(action.type.startsWith("studioSlice"))
      && !(action.type.startsWith("videoSlice"))
      && !(action.type.startsWith("studentSlice"))
      && !(action.type.startsWith("treatmentContentSlice"))
      && !(action.type.startsWith("articulationSlice"))
      && !(action.type.startsWith("h5pGamesSlice"))
      && !(action.type.startsWith("treatmentStudentSideSlice"))
      && !(action.type.startsWith("speechSlice"))
      && !(action.type.startsWith("youtubeSlice"))
      && !(action.type.startsWith("reading"))
      && !(action.type.startsWith("spelling"))
      && !(action.type.startsWith("chatSlice"))
      && !(action.type.startsWith("techStatus"))
    ) {
      return next(action);
    } // ignore non-treatment related actions

    if (action?.type === "websocketSlice/wsClose") {
      const user = currentUser(store.getState());
      if (user.role === ROLES.THERAPIST.id) {
        const treatment = selectTreatment(store.getState());
        treatment?.treatment_students && treatment.treatment_students.forEach(student => {
          store.dispatch(setBoardMode({ id: student.student_id }));
        });
      }
      socket?.readyState === WebSocket.OPEN && socket.sendJson({ command: WS_COMMANDS.BYE });
      store.dispatch(endVideoSession());
      store.dispatch(setIsOpentokError(true));
      clearInterval(pongInterval);
      store.dispatch(setUserStatuses([]));
      socket?.close?.(1000);
      return;
    }

    if (action?.type === "websocketSlice/wsConnect") {
      socket?.close?.(1000);
      socket = createWebsocket(store, action);
      return;
    }

    if (action?.type === "websocketSlice/wsReconnect") {
      socket.close(WS_ERROR_CODES.WS_RECONNECT_CODE);
      if (!window.location.href.includes("treatment/v3/")) {
        clearInterval(pongInterval);
        return;
      }
      socket = initWS(socket.url, socket.reduxStore);
      return;
    }
    
    if (action?.type === "websocketSlice/setPongInterval") {
      pongInterval = setInterval(() => {
        if (!window.location.href.includes("treatment/v3/")) {
          clearInterval(pongInterval);
          store.dispatch(wsClose());
          return;
        }
        const state = store.getState();
        const lastPong = selectLastPong(state);
        if (Date.now() - lastPong > 9000) {
          store.dispatch(wsReconnect());
          if (socket.readyState !== socket.OPEN) return;
          !showingBadNetwork && toast.info(<Trans>Weak internet connection</Trans>);
          showingBadNetwork = true;
        }
        if (Date.now() - lastPong > 15000) {
          store.dispatch(setOpentokReconnect(true));
        }
      }, 10000);
    }

    const state = store.getState();
    const currUser = currentUser(state);
    currUser.role === ROLES.THERAPIST.id && handleTherapsitTreatmentActions(state, action, socket);
    [ROLES.STUDENT.id, ROLES.CLINIC_ADMINISTRATOR.id].includes(currUser.role) && handleStudentTreatmentActions(state, action, socket);
    
    return next(action);
  };
};

export default wsMiddleware();
